/**
 * Makonaire Shop All
 * - dual-handle price slider (mouse, touch, keyboard)
 * - filter accordions, mobile filter drawer (focus trap, Esc, scroll lock)
 * - Shopify tag-URL filters (when no native tag filter exists)
 * - sort dropdowns, quantity controls, quick add / add to cart (uses the theme's cart events)
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-mka-shop-all]';

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (root.dataset.mkaReady === 'true') return;
      root.dataset.mkaReady = 'true';
      initPriceSliders(root);
      initAccordions(root);
      initFilterForms(root);
      initMobileDrawer(root);
      initQuantityControls(root);
      initCartButtons(root);
      initSort(root);
    });
  }

  /* ---------- Dual handle price slider ---------- */
  function initPriceSliders(root) {
    root.querySelectorAll('[data-mka-price-slider]').forEach(setupPriceSlider);
  }

  function setupPriceSlider(slider) {
    if (slider.classList.contains('is-disabled')) return;

    var track = slider.querySelector('[data-track]');
    var range = slider.querySelector('[data-range]');
    var handleMin = slider.querySelector('[data-handle-min]');
    var handleMax = slider.querySelector('[data-handle-max]');
    var labelMin = slider.querySelector('[data-label-min]');
    var labelMax = slider.querySelector('[data-label-max]');
    var inputMin = slider.querySelector('[data-input-min]');
    var inputMax = slider.querySelector('[data-input-max]');
    if (!track || !range || !handleMin || !handleMax) return;

    var minBound = Math.floor(parseFloat(slider.dataset.min) || 0);
    var maxBound = Math.ceil(parseFloat(slider.dataset.max) || 0);
    if (maxBound <= minBound) return;

    var valueMin = clamp(parseFloat(slider.dataset.valueMin), minBound, maxBound, minBound);
    var valueMax = clamp(parseFloat(slider.dataset.valueMax), minBound, maxBound, maxBound);
    if (valueMin > valueMax) valueMin = valueMax;

    var showPlaceholder = slider.dataset.placeholder === 'true';
    var formatter = makeFormatter(slider.dataset.currency);

    function clamp(v, lo, hi, fallback) {
      if (isNaN(v)) return fallback;
      return Math.min(Math.max(v, lo), hi);
    }

    function pct(v) {
      return ((v - minBound) / (maxBound - minBound)) * 100;
    }

    function render() {
      var pMin = pct(valueMin);
      var pMax = pct(valueMax);
      handleMin.style.left = pMin + '%';
      handleMax.style.left = pMax + '%';
      range.style.left = pMin + '%';
      range.style.width = Math.max(pMax - pMin, 0) + '%';

      handleMin.setAttribute('aria-valuenow', String(valueMin));
      handleMax.setAttribute('aria-valuenow', String(valueMax));
      handleMin.setAttribute('aria-valuemax', String(valueMax));
      handleMax.setAttribute('aria-valuemin', String(valueMin));
      handleMin.setAttribute('aria-valuetext', formatter(valueMin));
      handleMax.setAttribute('aria-valuetext', formatter(valueMax));

      if (!showPlaceholder) {
        if (labelMin) labelMin.textContent = formatter(valueMin);
        if (labelMax) labelMax.textContent = formatter(valueMax);
      }

      // Only send a value when the handle has actually been moved off its end
      if (inputMin) inputMin.value = valueMin <= minBound ? '' : String(valueMin);
      if (inputMax) inputMax.value = valueMax >= maxBound ? '' : String(valueMax);

      // When both handles meet, keep the one that can still move on top
      var atTopEnd = valueMin >= maxBound;
      handleMin.classList.toggle('is-top', atTopEnd || valueMin === valueMax && valueMin > (minBound + maxBound) / 2);
    }

    function setMin(v) {
      valueMin = Math.min(Math.max(Math.round(v), minBound), valueMax);
      render();
    }
    function setMax(v) {
      valueMax = Math.max(Math.min(Math.round(v), maxBound), valueMin);
      render();
    }

    function valueFromEvent(e) {
      var rect = track.getBoundingClientRect();
      var p = (e.clientX - rect.left) / rect.width;
      p = Math.max(0, Math.min(1, p));
      return minBound + p * (maxBound - minBound);
    }

    function bindHandle(handle, which) {
      handle.addEventListener('pointerdown', function (e) {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        handle.setPointerCapture(e.pointerId);
        handle.classList.add('is-dragging');
        handle.focus({ preventScroll: true });
      });
      handle.addEventListener('pointermove', function (e) {
        if (!handle.hasPointerCapture(e.pointerId)) return;
        var v = valueFromEvent(e);
        if (which === 'min') setMin(v);
        else setMax(v);
      });
      function end(e) {
        if (handle.hasPointerCapture && handle.hasPointerCapture(e.pointerId)) {
          handle.releasePointerCapture(e.pointerId);
        }
        handle.classList.remove('is-dragging');
      }
      handle.addEventListener('pointerup', end);
      handle.addEventListener('pointercancel', end);

      handle.addEventListener('keydown', function (e) {
        var step = Math.max(1, Math.round((maxBound - minBound) / 50));
        var delta = 0;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
        else if (e.key === 'PageDown') delta = -step * 5;
        else if (e.key === 'PageUp') delta = step * 5;
        else if (e.key === 'Home') delta = -Infinity;
        else if (e.key === 'End') delta = Infinity;
        else return;
        e.preventDefault();
        var current = which === 'min' ? valueMin : valueMax;
        var next = delta === -Infinity ? minBound : delta === Infinity ? maxBound : current + delta;
        if (which === 'min') setMin(next);
        else setMax(next);
      });
    }
    bindHandle(handleMin, 'min');
    bindHandle(handleMax, 'max');

    // Tap / click on the track moves the nearest handle
    track.addEventListener('pointerdown', function (e) {
      if (e.target === handleMin || e.target === handleMax) return;
      var v = valueFromEvent(e);
      if (Math.abs(v - valueMin) <= Math.abs(v - valueMax)) {
        setMin(v);
        handleMin.focus({ preventScroll: true });
      } else {
        setMax(v);
        handleMax.focus({ preventScroll: true });
      }
    });

    render();
  }

  function makeFormatter(currency) {
    try {
      if (currency) {
        var nf = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        return function (v) { return nf.format(v); };
      }
    } catch (err) { /* fall through */ }
    return function (v) { return '$' + Math.round(v); };
  }

  /* ---------- Accordions ---------- */
  function initAccordions(root) {
    root.querySelectorAll('[data-mka-accordion]').forEach(function (acc) {
      var header = acc.querySelector('[data-mka-accordion-header]');
      if (!header) return;
      header.addEventListener('click', function () {
        var open = acc.classList.toggle('is-open');
        header.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- Filter forms ----------
     Native filters submit as normal GET params. Tags that are not native filters
     use Shopify's tag URLs:  /collections/handle/tag-a+tag-b?sort_by=... */
  function initFilterForms(root) {
    root.querySelectorAll('[data-mka-filters-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        var tagInputs = form.querySelectorAll('input[data-mka-tag]');
        if (!tagInputs.length) return; // plain GET submit is fine

        e.preventDefault();
        var tags = [];
        tagInputs.forEach(function (i) { if (i.checked) tags.push(i.value); });

        var base = form.getAttribute('action') || window.location.pathname;
        base = base.replace(/\/+$/, '');
        var path = tags.length ? base + '/' + tags.join('+') : base;

        var params = new URLSearchParams();
        new FormData(form).forEach(function (value, key) {
          if (value !== '' && value !== null) params.append(key, value);
        });
        var qs = params.toString();
        window.location.href = path + (qs ? '?' + qs : '');
      });
    });
  }

  /* ---------- Mobile drawer ---------- */
  function initMobileDrawer(root) {
    var openBtn = root.querySelector('[data-mka-open-filters]');
    var drawer = root.querySelector('[data-mka-drawer]');
    if (!openBtn || !drawer) return;

    var closeBtn = drawer.querySelector('[data-mka-close-drawer]');
    var backdrop = drawer.querySelector('[data-mka-drawer-backdrop]');
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function open() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      openBtn.focus();
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key === 'Tab') {
        var items = Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
          return el.offsetParent !== null;
        });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Leaving mobile width with the drawer open must not leave the page locked
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 1024 && drawer.classList.contains('is-open')) close();
    });
  }

  /* ---------- Quantity ---------- */
  function initQuantityControls(root) {
    root.querySelectorAll('[data-mka-qty]').forEach(function (wrap) {
      var minus = wrap.querySelector('[data-mka-qty-minus]');
      var plus = wrap.querySelector('[data-mka-qty-plus]');
      var valueEl = wrap.querySelector('[data-mka-qty-value]');
      var input = wrap.querySelector('input[name="quantity"]');
      if (!valueEl || !input) return;

      var max = parseInt(wrap.dataset.max, 10);
      var hasMax = !isNaN(max) && max > 0;
      var qty = parseInt(input.value, 10) || 1;

      function update() {
        valueEl.textContent = qty;
        input.value = qty;
        if (minus) minus.disabled = qty <= 1;
        if (plus) plus.disabled = hasMax && qty >= max;
      }

      if (minus) minus.addEventListener('click', function () {
        if (qty > 1) { qty--; update(); }
      });
      if (plus) plus.addEventListener('click', function () {
        if (!hasMax || qty < max) { qty++; update(); }
      });
      update();
    });
  }

  /* ---------- Quick add / Add to cart ---------- */
  var eventsModulePromise = null;
  function getEvents() {
    if (!eventsModulePromise) {
      eventsModulePromise = import('@shopify/events').catch(function () { return null; });
    }
    return eventsModulePromise;
  }

  function cartAddUrl(form) {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) return window.Theme.routes.cart_add_url;
    return (form.getAttribute('action') || '/cart/add').replace(/\.js$/, '') + '.js';
  }

  function fetchCart() {
    var comp = document.querySelector('cart-items-component');
    if (comp && typeof comp.fetchCartData === 'function') return comp.fetchCartData();
    var url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    return fetch(url + '.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function initCartButtons(root) {
    root.querySelectorAll('[data-mka-quick-add], [data-mka-atc]').forEach(function (btn) {
      var form = btn.closest('form');
      if (!form) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (btn.disabled || btn.classList.contains('is-loading')) return;
        addToCart(form, btn);
      });
    });
  }

  async function addToCart(form, btn) {
    var original = btn.innerHTML;
    var isText = btn.hasAttribute('data-mka-atc');
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    var formData = new FormData(form);
    if (!formData.get('quantity')) formData.set('quantity', '1');

    // Ask the theme to hand back the cart drawer / cart page HTML in the same request
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (c) {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    var events = await getEvents();
    var deferred = null;
    var quantity = Number(formData.get('quantity')) || 1;

    if (events && events.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(formData.get('id')), quantity: quantity }],
          promise: deferred.promise,
        })
      );
    }

    try {
      var res = await fetch(cartAddUrl(form), {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      var data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      var ajaxCart = await fetchCart();
      if (deferred && events) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'makonaire-shop-all',
            sourceId: form.closest('[data-mka-card]') ? form.closest('[data-mka-card]').dataset.productId : '',
            itemCount: quantity,
            productId: form.closest('[data-mka-card]') ? form.closest('[data-mka-card]').dataset.productId : '',
            sections: data.sections,
            didError: false,
          },
        });
      } else {
        // Fallback if the theme event module is unavailable: update any cart count we can find
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      btn.innerHTML = isText ? 'Added ✓' : '✓';
      setTimeout(function () {
        btn.innerHTML = original;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }, 1400);
    } catch (err) {
      console.error('[makonaire-shop-all]', err);
      if (deferred) deferred.reject(err);
      btn.innerHTML = isText ? 'Try again' : '!';
      btn.classList.add('is-error');
      setTimeout(function () {
        btn.innerHTML = original;
        btn.classList.remove('is-loading', 'is-error');
        btn.removeAttribute('aria-busy');
      }, 1800);
    }
  }

  /* ---------- Sort (desktop + mobile selects) ---------- */
  function initSort(root) {
    var selects = root.querySelectorAll('[data-mka-sort]');
    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  /* ---------- boot ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  // Theme editor: re-run when the section is added / re-rendered
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();